// Fading line beam for abilities like Lux's Final Spark. Uses the hexes in
// the line to draw a wide glowing rectangle.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import { TIMING } from "../../config/constants";
import type { Point } from "../../engine/hex/HexLayout";

export class BeamVFX implements Animation {
  private elapsed = 0;
  private duration = TIMING.abilityWindup + TIMING.abilityActive;

  constructor(
    private from: Point,
    private to: Point,
    private color: string = "#f0e6d2",
    private width: number = 32,
  ) {}

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, this.duration);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const progress = this.elapsed / this.duration;
    const dx = this.to.x - this.from.x;
    const dy = this.to.y - this.from.y;
    const len = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx);

    ctx.save();
    ctx.translate(this.from.x, this.from.y);
    ctx.rotate(angle);

    if (progress < TIMING.abilityWindup / this.duration) {
      // Windup — thin charging line.
      const p = progress / (TIMING.abilityWindup / this.duration);
      ctx.globalAlpha = 0.3 + p * 0.4;
      ctx.fillStyle = this.color;
      ctx.fillRect(0, -1, len * Easing.easeOutQuad(p), 2);
    } else {
      // Active — expand then fade.
      const activeP =
        (progress - TIMING.abilityWindup / this.duration) /
        (TIMING.abilityActive / this.duration);
      const widthFactor = Math.sin(Math.PI * activeP); // rise and fall
      const alpha = 1 - activeP * 0.6;
      const w = this.width * widthFactor + 4;

      ctx.shadowColor = this.color;
      ctx.shadowBlur = 24;
      ctx.globalAlpha = alpha * 0.4;
      ctx.fillStyle = this.color;
      ctx.fillRect(0, -w / 2, len, w);

      // Hot core
      ctx.globalAlpha = alpha;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, -w / 6, len, w / 3);
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= this.duration;
  }
}
